import { MouseEvent, ReactNode, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import { ImagePicture } from 'approot/shared/images/images';
import {
  MenuCategory,
  MenuItem,
  MenuData,
  AddComponentTemplate,
  ComponentMenuRenderProp,
} from './components-menu.constants';
import {
  ComponentPlusLine,
  ComponentPlusToggle,
  ComponentPlusWrapper,
} from '../components.style';
import { useComponentMenu } from './component-menu.hooks';
import * as Styled from './components-add-menu.style';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import {
  ACTIVITY_BUILDER_DRAWER_ADD_COMPONENT,
  ACTIVITY_BUILDER_DRAWER_ID,
  ACTIVITY_BUILDER_GLOBAL_ADD,
} from 'approot/activity-builder/activity-builder.constants';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import {
  MultiView,
  MultiViewPage,
} from 'approot/shared/layout/layout-multi-view';
import {
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { DraggableMenuComponent } from 'approot/activity-builder/drag-drop/draggable-menu-component';
import { formatToLowerKebabCase } from 'approot/shared/logic/format-to-lower-kebab-case';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import IconSearch from 'approot/shared/icons/icon-search';
import ComponentAddStimulusMenu from './component-add-stimulus-menu';

const MainMenu = ({
  menuItems,
  onClose,
  onClick,
}: {
  menuItems: MenuData[];
  onClose: () => void;
  onClick: (code: MenuCategory) => void;
}) => (
  <ActivityBuilderDrawerWrapper>
    <ActivityBuilderDrawerHeader hasBorderBottom={false} onClose={onClose} />
    <ActivityBuilderDrawerContent padding={0}>
      <Styled.ComponentAddMenuItemList>
        {menuItems.map(menu => (
          <li key={menu.section}>
            <Styled.ComponentAddMenuItem
              type="button"
              onClick={() => onClick(menu.section)}
              data-selector={`component-item-${formatToLowerKebabCase(
                menu.label
              )}`}
            >
              {menu.label}
            </Styled.ComponentAddMenuItem>
          </li>
        ))}
      </Styled.ComponentAddMenuItemList>
    </ActivityBuilderDrawerContent>
  </ActivityBuilderDrawerWrapper>
);

const SecondaryMenu = ({
  menuLabel,
  menuItems,
  extraContent,
  onClose,
}: {
  menuLabel?: string;
  menuItems: MenuItem[];
  extraContent?: ReactNode;
  onClose: () => void;
}) => (
  <Styled.ComponentAddMenuWrapper>
    <ActivityBuilderDrawerHeader
      isBack
      title={menuLabel || ''}
      onClose={onClose}
    />
    <ActivityBuilderDrawerContent padding={0}>
      {extraContent}
      <Styled.ComponentAddMenuItemList>
        {menuItems.map(item => (
          <li key={item.id}>
            <DraggableMenuComponent
              id={item.id}
              type={item.data.componentProperties.type}
              menuItem={item}
            >
              <Styled.ComponentAddMenuSubItem
                type="button"
                data-selector={`${formatToLowerKebabCase(item.name)}`}
                aria-label={`Add ${item.name} to your activity`}
                onClick={() => item.onClick()}
              >
                <Styled.ComponentAddMenuImage>
                  <ImagePicture
                    jpg={{
                      path:
                        item.previews.find(p => p.format === 'jpg')?.url || '',
                    }}
                    avif={{
                      path:
                        item.previews.find(p => p.format === 'avif')?.url || '',
                    }}
                    webp={{
                      path:
                        item.previews.find(p => p.format === 'webp')?.url || '',
                    }}
                  />
                </Styled.ComponentAddMenuImage>
                <Styled.ComponentAddMenuItemText data-selector="component-item-name">
                  {item.name}
                </Styled.ComponentAddMenuItemText>
                {item.description && (
                  <Styled.ComponentAddMenuItemDescription data-selector="component-item-description">
                    {item.description}
                  </Styled.ComponentAddMenuItemDescription>
                )}
              </Styled.ComponentAddMenuSubItem>
            </DraggableMenuComponent>
          </li>
        ))}
      </Styled.ComponentAddMenuItemList>
    </ActivityBuilderDrawerContent>
  </Styled.ComponentAddMenuWrapper>
);

const SecondaryMenuLayer = ({
  secondMenuCategory,
  menuItems,
  onClose,
}: {
  secondMenuCategory?: MenuCategory;
  menuItems: MenuData[];
  onClose: () => void;
}) => {
  return (
    <MultiView>
      {multiviewProps => (
        <>
          <MultiViewPage
            index={0}
            {...multiviewProps}
            onClose={onClose}
            renderItem={() => (
              <SecondaryMenu
                extraContent={
                  secondMenuCategory === MenuCategory.Stimulus && (
                    <Styled.ComponentAddMenuSearch>
                      <SecondaryButton
                        type="button"
                        $withIcon
                        data-selector="btn-search-all-stimulus"
                        onClick={() => multiviewProps.goTo(1)}
                      >
                        <IconSearch width={20} height={20} />
                        Search All Stimulus
                      </SecondaryButton>
                    </Styled.ComponentAddMenuSearch>
                  )
                }
                menuLabel={
                  menuItems.find(m => m.section === secondMenuCategory)?.label
                }
                menuItems={
                  menuItems.find(m => m.section === secondMenuCategory)
                    ?.items || []
                }
                onClose={onClose}
              />
            )}
          />
          <MultiViewPage
            index={1}
            {...multiviewProps}
            onClose={multiviewProps.goBack}
            renderItem={() => (
              <ComponentAddStimulusMenu
                menuItems={menuItems}
                goBack={multiviewProps.goBack}
              />
            )}
          />
        </>
      )}
    </MultiView>
  );
};

export const ComponentMenu = ({
  menuItems,
  canRenderMenu,
  render,
}: {
  menuItems: MenuData[];
  canRenderMenu: boolean;
} & ComponentMenuRenderProp) => {
  const [secondMenuCategory, setSecondMenuCategory] = useState<
    MenuCategory | undefined
  >();

  const { openDrawer, closeDrawer, isAddDrawerActive } =
    useActivityBuilderContext();

  const el = document.getElementById(ACTIVITY_BUILDER_DRAWER_ID);

  const showDrawer = el && isAddDrawerActive() && canRenderMenu;

  const handleOpenMenu = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      openDrawer({
        componentId: ACTIVITY_BUILDER_DRAWER_ADD_COMPONENT,
      });
    },
    [openDrawer]
  );

  const handleCloseDrawer = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  return (
    <>
      {showDrawer &&
        createPortal(
          <MultiView>
            {multiviewProps => (
              <>
                <MultiViewPage
                  index={0}
                  {...multiviewProps}
                  onClose={handleCloseDrawer}
                  renderItem={() => (
                    <MainMenu
                      menuItems={menuItems}
                      onClick={value => {
                        setSecondMenuCategory(value);
                        multiviewProps.goForward();
                      }}
                      onClose={handleCloseDrawer}
                    />
                  )}
                />
                <MultiViewPage
                  index={1}
                  {...multiviewProps}
                  onClose={multiviewProps.goBack}
                  renderItem={() => (
                    <SecondaryMenuLayer
                      secondMenuCategory={secondMenuCategory}
                      menuItems={menuItems}
                      onClose={multiviewProps.goBack}
                    />
                  )}
                />
              </>
            )}
          </MultiView>,
          el
        )}

      {render(handleOpenMenu)}
    </>
  );
};

export const ComponentsAddMenu = ({
  canRenderMenu,
  render,
  addComponent,
}: {
  canRenderMenu: boolean;
  addComponent: (props: AddComponentTemplate) => void;
} & ComponentMenuRenderProp) => {
  const { menu } = useComponentMenu({ addComponent });
  return (
    <ComponentMenu
      menuItems={menu}
      render={render}
      canRenderMenu={canRenderMenu}
    />
  );
};

export const ComponentAddTargetArea = () => <Styled.ComponentAddMenuTarget />;

export const ComponentAddMenuTarget = ({
  componentId,
  isLastComponent,
  isAlwaysVisible,
  addComponent,
}: {
  componentId: string;
  isLastComponent: boolean;
  isAlwaysVisible?: boolean;
  addComponent: (props: AddComponentTemplate) => void;
}) => {
  const { activeComponent, setActiveComponent, isAddDrawerActive } =
    useActivityBuilderContext();

  return (
    <ComponentsAddMenu
      addComponent={addComponent}
      canRenderMenu={isAddDrawerActive() && activeComponent?.id === componentId}
      render={onClick => {
        const isAddTarget =
          isAddDrawerActive() &&
          (activeComponent?.id === componentId ||
            (activeComponent?.id === ACTIVITY_BUILDER_GLOBAL_ADD &&
              isLastComponent));

        return isAddTarget ? (
          <ComponentAddTargetArea />
        ) : (
          <ComponentPlusWrapper $isAlwaysVisible={isAlwaysVisible}>
            <ComponentPlusLine />
            <ComponentPlusToggle
              data-selector="add-component"
              aria-label="Add a component"
              onClick={e => {
                setActiveComponent({
                  id: componentId,
                });
                onClick(e);
              }}
            >
              <ActivityBuilderInlineSvg
                id="icon_plus_active"
                width={20}
                height={20}
              />
            </ComponentPlusToggle>
          </ComponentPlusWrapper>
        );
      }}
    />
  );
};

export const ComponentAddMenuIconButton = ({
  onClick,
}: {
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
}) => {
  return (
    <TooltipWrapper hoverText="Add item">
      <Styled.ComponentAddMenuButton
        type="button"
        aria-label="Add a component"
        data-selector="settings-menu-add"
        $noBorder
        $isSmall
        onClick={onClick}
      >
        <ActivityBuilderInlineSvg
          id="icon_plus_active"
          width={20}
          height={20}
        />
      </Styled.ComponentAddMenuButton>
    </TooltipWrapper>
  );
};
